import { cancelledStatus, filledStatus, inactiveStatus, openStatus, rejectedStatus } from "../assets/colors"
import { Order, OrderStatus, OrderType } from "../proto/generated/order"

/** Basket (and quick-trade) ancillary legs: SL, TP, conditional entry/exit — not primary entry legs. */
export const ORDER_TYPES_STRATEGY_AUXILIARY: OrderType[] = [
    OrderType.STOP_LOSS,
    OrderType.TARGET,
    OrderType.CONDITIONAL_ENTRY,
    OrderType.CONDITIONAL_EXIT,
]
import { Security } from "../proto/generated/security"
import { addOrderFiredAlert, OrdersDataType } from "../reducer/orders.reducer"
import store from "../store"

export function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b)
}

export function calculateGcd(arr: number[]) {
    if(arr.length===0) return 0
    const gcdOfArray: number = arr.reduce(gcd)
    return gcdOfArray
}

export const findOrderPremium = (order: OrdersDataType, security: Security | undefined): string => {
    let result
    if(order.contractType === 'FUTURE') return '0'
    if (order.type === 'sell') {
        if (order.callput === 'call') {
            result = order.data.callOption?.topBid
        } else {
            result = order.data.putOption?.topBid
        }
    } else {
        if (order.callput === 'call') {
            result = order.data.callOption?.topAsk
        } else {
            result = order.data.putOption?.topAsk
        }
    }
    result = (result ?? 0) / (security?.priceMultiplier ?? 100)
    return String(result)
}

export const addOrderAlertMessage = (order: Order) => {
    store.dispatch(addOrderFiredAlert({
        ...order,
        exchangeTimestamp: Date.now() - 100
    }))
}

export const getRoundedPrice = (priceAsInt: number, tickSize = 5) => Math.round( priceAsInt/ tickSize) * tickSize;


export const getOrderStatusColor = (status: OrderStatus) => {
    switch (status) {
        case OrderStatus.OPEN:
        case OrderStatus.PENDING:
        case OrderStatus.TRIGGER_PENDING:
            return openStatus;
        case OrderStatus.EXECUTED:
        case OrderStatus.PARTIALLY_FILLED:
            return filledStatus;
        case OrderStatus.INACTIVE:
            return inactiveStatus;
        case OrderStatus.CANCELLED:
            return cancelledStatus;
        case OrderStatus.FAILED:
        case OrderStatus.REJECTED:
            return rejectedStatus;
        default:
            return "black";
    }   
}


const orderStatusTextMap: Record<OrderStatus, string> = {
    [OrderStatus.OPEN]: "Open",
    [OrderStatus.PENDING]: "Pending",
    [OrderStatus.TRIGGER_PENDING]: "Trig. Pending",
    [OrderStatus.EXECUTED]: "Filled",
    [OrderStatus.PARTIALLY_FILLED]: "Part. Filled",
    [OrderStatus.INACTIVE]: "Inactive",
    [OrderStatus.CANCELLED]: "Cancelled",
    [OrderStatus.FAILED]: "Failed",
    [OrderStatus.REJECTED]: "Rejected",
    [OrderStatus.ORDER_STATUS_UNKNOWN]: "-",
    [OrderStatus.UNRECOGNIZED]: "-"

}

export const getOrderStatusText = (status: OrderStatus) => {
    return orderStatusTextMap[status]
}

/** Broker placeholder or empty strategy label — resolve from another basket leg instead. */
export function isPlaceholderStrategyGroupName(name: string | undefined): boolean {
    const t = name?.trim().toLowerCase()
    return !t || t === 'default'
}

/** Strategy group from any basket main leg; when `batchId` is set, restrict to that placement wave. */
export function resolveBasketBatchStrategyGroupName(
    orders: Order[] | undefined,
    basketId: string | undefined,
    batchId?: string | undefined
): string | undefined {
    if (!orders?.length || !basketId) return undefined
    const main = orders.find(
        (o) =>
            o.basketId === basketId &&
            (batchId === undefined || batchId === '' || o.batchId === batchId) &&
            o.isBasket &&
            !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            !isPlaceholderStrategyGroupName(o.strategyGroup)
    )
    const sg = main?.strategyGroup?.trim()
    return sg && !isPlaceholderStrategyGroupName(sg) ? sg : undefined
}

/** Main Orders widget tab keys (align with `OrdersNavButtonsDetailed`, kept in util to avoid circular UI imports). */
export type OrdersWidgetTableView = 'OPEN' | 'COMPLETE' | 'REJECTED' | 'CANCELLED' | 'ALL'

const ORDERS_WIDGET_OPEN_ACTIVE_STATUSES: OrderStatus[] = [
    OrderStatus.OPEN,
    OrderStatus.PENDING,
    OrderStatus.PARTIALLY_FILLED,
    OrderStatus.TRIGGER_PENDING,
    OrderStatus.INACTIVE,
]

const TERMINAL_STATUS_FOR_ORDERS_WIDGET: OrderStatus[] = [
    OrderStatus.EXECUTED,
    OrderStatus.CANCELLED,
    OrderStatus.REJECTED,
    OrderStatus.FAILED,
]

export function isBasketBatchOrder(o: Order): boolean {
    return Boolean(o.isBasket && o.basketId && o.batchId)
}

export function basketBatchKey(o: Order): string | undefined {
    if (!isBasketBatchOrder(o)) return undefined
    return `${o.basketId}|${o.batchId}`
}

// returns basketBatch incomplete legs
function incompleteBatchKeySetForOrdersWidget(orders: Order[]): Set<string> {
    const incompleteBatchKeys = new Set<string>()
    for (const o of orders) {
        if (!o.basketId || !o.batchId) continue
        if (o.orderExecutionParams?.icebergNumberOfLegs == null) continue
        if (!TERMINAL_STATUS_FOR_ORDERS_WIDGET.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)) {
            incompleteBatchKeys.add(`${o.basketId}|${o.batchId}`)
        }
    }
    return incompleteBatchKeys
}

/** Non-basket rows only: same rules as legacy Open tab (incl. incomplete iceberg batch + EXECUTED visibility). */
function orderMatchesOpenOrdersTabNonBasket(o: Order, incompleteBatchKeys: Set<string>): boolean {
    const st = o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
    if (ORDERS_WIDGET_OPEN_ACTIVE_STATUSES.includes(st)) return true
    if (st === OrderStatus.EXECUTED && o.basketId && o.batchId) {
        return incompleteBatchKeys.has(`${o.basketId}|${o.batchId}`)
    }
    return false
}

function filterOrdersWidgetOpen(orders: Order[]): Order[] {
    if (!orders?.length) return []
    const incompleteBatchKeys = incompleteBatchKeySetForOrdersWidget(orders)

    const qualifyingBasketKeys = new Set<string>()
    for (const o of orders) {
        if (!isBasketBatchOrder(o)) continue
        const st = o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
        if (ORDERS_WIDGET_OPEN_ACTIVE_STATUSES.includes(st)) {
            const k = basketBatchKey(o)
            if (k) qualifyingBasketKeys.add(k)
        }
    }

    return orders.filter((o) => {
        // either open
        if (isBasketBatchOrder(o)) {
            const k = basketBatchKey(o)
            return k !== undefined && qualifyingBasketKeys.has(k)
        }
        // or executed but in a basket or iceberg
        return orderMatchesOpenOrdersTabNonBasket(o, incompleteBatchKeys)
    })
}

function filterOrdersWidgetByTerminalStatus(orders: Order[], status: OrderStatus): Order[] {
    const qualifyingBasketKeys = new Set<string>()
    for (const o of orders) {
        if (!isBasketBatchOrder(o)) continue
        if ((o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN) === status) {
            const k = basketBatchKey(o)
            if (k) qualifyingBasketKeys.add(k)
        }
    }
    return orders.filter((o) => {
        if (isBasketBatchOrder(o)) {
            const k = basketBatchKey(o)
            return k !== undefined && qualifyingBasketKeys.has(k)
        }
        return (o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN) === status
    })
}

/**
 * Orders widget tab filter: basket batches appear in a tab if any leg qualifies; all legs in that batch are included.
 * Non-basket rows use per-order predicates (Open preserves legacy non-basket iceberg behavior).
 */
export function filterOrdersForOrdersWidgetTab(orders: Order[], tab: OrdersWidgetTableView): Order[] {
    if (!orders?.length) return []
    switch (tab) {
        case 'ALL':
            return orders
        case 'OPEN':
            return filterOrdersWidgetOpen(orders)
        case 'COMPLETE':
            return filterOrdersWidgetByTerminalStatus(orders, OrderStatus.EXECUTED)
        case 'REJECTED':
            return filterOrdersWidgetByTerminalStatus(orders, OrderStatus.REJECTED)
        case 'CANCELLED':
            return filterOrdersWidgetByTerminalStatus(orders, OrderStatus.CANCELLED)
        default:
            return orders
    }
}